// openCapsuleClient — the UNIFIED "open this capsule" client (launch-unification
// plan v3, §3). Every user-facing open/run/stop/cancel goes through here and
// ONLY through POST/GET /v1/launches — never raw /v1/runs, never web-runs.
//
//   openCapsule(ref, opts) → POST /v1/launches → (desktop handoff gate) →
//                            single poll loop → OpenCapsuleResult
//   stop(launchId)         → POST /v1/launches/:id/stop   (ready/running)
//   cancel(launchId)       → POST /v1/launches/:id/cancel (blocked/starting)
//
// One poll implementation, one timeout policy (~120s hard stop, fast→slow
// cadence), one error map (401/402/404/409/422/429 + blockers passthrough),
// and the SINGLE 402 stash call site (reuses cloudLaunchStash).
//
// Auth is the account session cookie: credentials "include", fetch bound to
// globalThis (never unbound — Workers/undici throw on an unbound fetch).

import {
  LaunchApiError,
  LaunchConnectionError,
  launchApiErrorFromBody,
  defaultBaseUrl,
  type EmbedPolicy,
  type LaunchPaywallOffer,
} from "./launchClient";
import { markSnapshotTiming } from "./snapshotClient";
import { analyticsIdentityHeaders } from "@/analytics/client";
import type { RunOrigin } from "@/analytics/events";
import {
  PWA_ACCEPTED_SESSION_SURFACES,
  sessionSurfaceFromEnvelope,
  type SessionSurface,
} from "./sessionSurface";
import { savePendingCloudLaunch } from "../auth/cloudLaunchStash";
import { requestDesktopLaunch, type DesktopLaunchAck } from "../desktop/bridge";
import { withConnectionAuth } from "../app/connection";

const ROUTE = "/v1/launches";

// ── v3 contract vocabulary (mirrors ato-api launchesRoutes field-for-field) ──

export type LaunchState =
  | "blocked"
  | "starting"
  | "ready"
  | "failed"
  | "stopping"
  | "stopped"
  | "cancelled"
  | "expired";

export type LaunchExecution =
  | { kind: "auto" }
  | { kind: "snapshot"; snapshot_id?: string }
  | { kind: "source" };

export type LaunchPlacement =
  | { kind: "auto" }
  | { kind: "ato_cloud" }
  | { kind: "personal_runner"; runner_id?: string }
  | { kind: "team_runner"; runner_id?: string }
  | { kind: "specific_runner"; runner_id: string };

/** DIAGNOSTICS/ANALYTICS ONLY on the server — never feeds authz or billing.
 *  One shared vocabulary with the analytics RunOrigin (events.ts), so the
 *  launch API and the traffic-source aggregation cannot disagree. */
export type LaunchOrigin = RunOrigin;

export type LaunchBlocker =
  | { kind: "snapshot_not_ready"; prepare_available: boolean }
  | { kind: "unsupported_snapshot"; reason: string }
  | { kind: "no_eligible_runner" }
  | { kind: "cloud_quota_exceeded" }
  | { kind: "billing_required" }
  | { kind: "consent_required"; preview_id: string }
  | { kind: "auth_required" };

/** The server's launch DTO (launchResponse in ato-api). Field names EXACT. */
export interface LaunchStatus {
  launch_id: string;
  state: LaunchState;
  capsule_ref?: string;
  target_label?: string;
  profile?: string;
  selected_provider?: string | null;
  run_id?: string | null;
  blockers?: LaunchBlocker[];
  status_url?: string;
  cancel_url?: string;
  stop_url?: string;
  /** Session Surface Contract v1. A present surface is authoritative. */
  surface_contract_version?: unknown;
  surface?: unknown;
  /** Legacy Web projection. New Web responses dual-write it during migration. */
  app_url?: string;
  app_expires_at?: string;
  embed_policy?: EmbedPolicy;
  app_origin?: string;
  error?: { code: string; message?: string };
}

// ── OpenApp: the shape a ready launch resolves to (plan §3) ─────────────────

/**
 * A ready (or opening) app launch returned to the caller. Identity = `launch_id`;
 * `app_url` is a view URL that can change (proxy update, restore, handoff) and
 * must never be used as a key.
 */
export interface OpenApp {
  launch_id?: string;
  run_id?: string;
  capsule_ref?: string;
  title: string;
  icon_url?: string;
  state: "starting" | "ready" | "failed" | "stopped";
  /** View URL only — may change; not an identity. */
  app_url?: string;
  /** Tagged live-surface contract used by surface-aware PWA renderers. */
  surface?: SessionSurface;
  open_mode: "embedded" | "desktop_native" | "external";
  /** Rendering hint for the embedded console (server-computed). */
  embed_policy?: EmbedPolicy;
  stop_url?: string;
}

export type ReadyOpenApp = OpenApp & {
  state: "ready";
  surface: SessionSurface;
};

// ── error map (ONE implementation) ──────────────────────────────────────────

export type OpenCapsuleErrorKind =
  | "auth_required" // 401 — sign in, then retry
  | "billing_required" // 402 — paywall (pending launch already stashed)
  | "not_found" // 404 — capsule/launch not available
  | "conflict" // 409 — already active / teardown race
  | "unsupported" // 422 — no runner has the needed capabilities
  | "rate_limited" // 429
  | "timeout" // poll hard stop
  | "aborted" // caller aborted the poll
  | "network" // could not reach the API
  | "error"; // everything else

export class OpenCapsuleError extends Error {
  constructor(
    readonly kind: OpenCapsuleErrorKind,
    message: string,
    readonly detail: {
      status?: number;
      code?: string;
      /** Typed blockers echoed by a blocked-launch error body (e.g. the 402). */
      blockers?: LaunchBlocker[];
      /** The blocked launch's id, when the error body carried one (resume). */
      launchId?: string;
      /** 422 → the runner capabilities the capsule needs. */
      missingCapabilities?: string[];
      /** 402 → plan offer for a Subscribe CTA. */
      offer?: LaunchPaywallOffer;
    } = {},
  ) {
    super(message);
    this.name = "OpenCapsuleError";
  }
}

/** HTTP status → error kind (the single mapping table). */
function kindForStatus(status: number): OpenCapsuleErrorKind {
  switch (status) {
    case 401:
      return "auth_required";
    case 402:
      return "billing_required";
    case 404:
      return "not_found";
    case 409:
      return "conflict";
    case 422:
      return "unsupported";
    case 429:
      return "rate_limited";
    default:
      return "error";
  }
}

/** Blockers/launch_id passthrough from an error body (a blocked launch keeps
 *  its typed blockers even under a legacy HTTP status like the 402). */
function blockedDetailFromBody(json: unknown): {
  blockers: LaunchBlocker[];
  launchId?: string;
} {
  const j = json as Record<string, unknown> | null;
  const blockers = Array.isArray(j?.["blockers"])
    ? (j!["blockers"] as LaunchBlocker[])
    : [];
  const launchId =
    typeof j?.["launch_id"] === "string"
      ? (j["launch_id"] as string)
      : undefined;
  return { blockers, launchId };
}

/**
 * Detect a Static Web delivery in a /v1/launches POST response (vertical
 * slice). The server returns `{ delivery: { kind: "static_web", ... },
 * run_id: null, ... }` instead of a launch when an active production static
 * deployment exists. Returns the normalized delivery, or null when the body is
 * a normal launch. Malformed deliveries fail closed to null (never treated as
 * static).
 */
function normalizeLaunchStaticDelivery(json: unknown): {
  appUrl: string;
  hostLabel: string;
  manifestDigest: string;
  materializationId: string;
  capsuleRevisionId: string;
  environment: "production";
  immutable: true;
} | null {
  const root = json as Record<string, unknown> | null;
  const r = root?.["delivery"] as Record<string, unknown> | null;
  if (!r || r["kind"] !== "static_web") return null;
  const appUrl = typeof r["app_url"] === "string" ? r["app_url"] : null;
  const hostLabel =
    typeof r["host_label"] === "string" ? r["host_label"] : null;
  const manifestDigest =
    typeof r["manifest_digest"] === "string" ? r["manifest_digest"] : null;
  const materializationId =
    typeof r["materialization_id"] === "string"
      ? r["materialization_id"]
      : null;
  const capsuleRevisionId =
    typeof r["capsule_revision_id"] === "string"
      ? r["capsule_revision_id"]
      : null;
  if (
    !appUrl ||
    !hostLabel ||
    !manifestDigest ||
    !materializationId ||
    !capsuleRevisionId ||
    r["environment"] !== "production" ||
    r["immutable"] !== true
  ) {
    return null;
  }
  return {
    appUrl,
    hostLabel,
    manifestDigest,
    materializationId,
    capsuleRevisionId,
    environment: "production",
    immutable: true,
  };
}

// ── results ─────────────────────────────────────────────────────────────────

export type OpenCapsuleResult =
  /** The launch is ready — embed/open `app`. */
  | { kind: "ready"; app: ReadyOpenApp; launch: LaunchStatus }
  /** Ato Desktop accepted the launch handoff; it owns visibility now
   *  (the PWA did NOT poll and must not render the app). */
  | { kind: "handed_off"; app: OpenApp; launch_id: string }
  /** The launch persisted blocked (run_id = null) — resumable by re-POST. */
  | {
      kind: "blocked";
      launch_id: string;
      blockers: LaunchBlocker[];
      launch: LaunchStatus;
    }
  /** The launch reached a terminal non-ready state while we watched. */
  | {
      kind: "failed";
      state: Extract<
        LaunchState,
        "failed" | "stopping" | "stopped" | "cancelled" | "expired"
      >;
      launch: LaunchStatus;
    }
  /** A Static Web delivery (vertical slice): the server returned a CDN URL
   *  instead of a workload. There is no launch to poll, stop, or continue. */
  | {
      kind: "static_web";
      appUrl: string;
      hostLabel: string;
      manifestDigest: string;
      materializationId: string;
      capsuleRevisionId: string;
      environment: "production";
      immutable: true;
    };

export interface OpenCapsuleOptions {
  execution?: LaunchExecution;
  placement?: LaunchPlacement;
  origin?: LaunchOrigin;
  targetLabel?: string;
  profile?: string;
  idempotencyKey?: string;
  /** BYOK (billing v2): launch with the user's OWN stored provider API key —
   *  free for a priced capsule. The server 400s (`ai_key_required`) when no
   *  key is on file. */
  aiByok?: boolean;
  /** Observe every status the poll sees (drive progress UI). */
  onUpdate?: (launch: LaunchStatus) => void;
  /** Return true to abort the poll (component unmount / user cancel). */
  shouldAbort?: () => boolean;
}

// ── poll policy (the ONE timeout policy) ────────────────────────────────────

/** Hard stop for the whole open→ready wait. */
const HARD_STOP_MS = 120_000;
/** Fast cadence while the app usually comes up. Track L (ato#948): a snapshot
 *  restore reaches ready in well under a second on the runner, so the poll
 *  cadence IS the user-visible latency — 300ms costs ≤150ms average overhead
 *  vs. the ~500ms a 1s cadence left on the table. */
const FAST_POLL_MS = 300;
/** …then back off after this long… */
const FAST_WINDOW_MS = 10_000;
/** …to this cadence for the rest of the wait. */
const SLOW_POLL_MS = 3_000;

const defaultSleep = (ms: number) =>
  new Promise<void>((r) => setTimeout(r, ms));

export interface OpenCapsuleClientConfig {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  // ── test seams (defaults are the real policy) ──
  hardStopMs?: number;
  fastPollMs?: number;
  fastWindowMs?: number;
  slowPollMs?: number;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  /** Desktop handoff seam; defaults to the real bridge helper (which returns
   *  not-accepted outside Ato Desktop / on older desktops / on timeout). */
  desktopLaunch?: (
    launchId: string,
    capsuleRef?: string,
  ) => Promise<DesktopLaunchAck>;
}

// The app-wide default client instance (lazy). Components take an optional
// `openClient` prop for tests and fall back to this shared instance, so every
// surface converges on ONE client (and one poll/error policy) by default.
let sharedClient: OpenCapsuleClient | null = null;
/** A fresh per-submission idempotency key (crypto.randomUUID where available —
 *  browsers, Workers, Node 19+ — else a time+random fallback). */
function newIdempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `lk_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

export function defaultOpenCapsuleClient(): OpenCapsuleClient {
  return (sharedClient ??= new OpenCapsuleClient());
}

// ── run_id → launch_id index (stop-facade bridge) ───────────────────────────
//
// Run summaries (GET /v1/runs) carry no launch_id, and the API has no
// launch-lookup-by-run endpoint yet — but every launch THIS client watches
// reports its dispatched run_id. Remembering that pairing lets every stop
// path route through POST /v1/launches/:id/stop (plan §2.1) for anything
// launched here, instead of raw-stopping /v1/runs. sessionStorage-backed so
// a reload keeps the mapping; the in-memory map is the storage-less fallback.
// Runs launched elsewhere stay unknown — their legacy stop is the documented
// Phase-4 remainder (needs a server-side lookup).

const LAUNCH_BY_RUN_KEY = "ato_launch_by_run";
const LAUNCH_BY_RUN_MAX = 50;
// In-memory fallback, used ONLY when sessionStorage is unavailable (private
// mode). sessionStorage is otherwise the single source of truth — so clearing
// it (tests, sign-out sweeps) really clears the index.
const launchByRunMemory = new Map<string, string>();

/** null = sessionStorage unavailable (fall back to the memory map). */
function readLaunchByRunStore(): Record<string, string> | null {
  try {
    const raw = sessionStorage.getItem(LAUNCH_BY_RUN_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, string>;
    }
    return {};
  } catch {
    return null;
  }
}

/** Record that `runId` is backed by `launchId` (called by the client for every
 *  status it observes that carries a run_id). */
export function rememberLaunchForRun(runId: string, launchId: string): void {
  const store = readLaunchByRunStore();
  if (store === null) {
    launchByRunMemory.set(runId, launchId);
    return;
  }
  try {
    if (store[runId] === launchId) return;
    store[runId] = launchId;
    // Bound the map: drop oldest insertions beyond the cap.
    const keys = Object.keys(store);
    for (let i = 0; i < keys.length - LAUNCH_BY_RUN_MAX; i++) {
      delete store[keys[i]];
    }
    sessionStorage.setItem(LAUNCH_BY_RUN_KEY, JSON.stringify(store));
  } catch {
    launchByRunMemory.set(runId, launchId);
  }
}

/** The launch backing `runId`, when one was observed by this client (this
 *  browsing session); null for runs launched elsewhere. */
export function launchIdForRun(runId: string): string | null {
  const store = readLaunchByRunStore();
  if (store === null) return launchByRunMemory.get(runId) ?? null;
  const stored = store[runId];
  return typeof stored === "string" ? stored : null;
}

/**
 * Stop the launch backing `runId` through the /v1/launches stop facade
 * (cancel as the pre-v3-server fallback). Returns true when the stop was
 * handled via the facade; false when no launch is known for this run — the
 * caller then falls back to its legacy /v1/runs stop (runs launched outside
 * this client; server-side lookup is the Phase-4 cleanup).
 */
export async function stopRunLaunch(
  client: OpenCapsuleClient,
  runId: string,
): Promise<boolean> {
  const launchId = launchIdForRun(runId);
  if (!launchId) return false;
  try {
    await client.stop(launchId);
  } catch {
    await client.cancel(launchId);
  }
  return true;
}

export class OpenCapsuleClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly hardStopMs: number;
  private readonly fastPollMs: number;
  private readonly fastWindowMs: number;
  private readonly slowPollMs: number;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly now: () => number;
  private readonly desktopLaunch: (
    launchId: string,
    capsuleRef?: string,
  ) => Promise<DesktopLaunchAck>;

  constructor(config: OpenCapsuleClientConfig = {}) {
    this.baseUrl = (config.baseUrl ?? defaultBaseUrl()).replace(/\/$/, "");
    // NEVER an unbound fetch — an unbound call throws "Illegal invocation".
    this.fetchImpl = config.fetchImpl ?? fetch.bind(globalThis);
    this.hardStopMs = config.hardStopMs ?? HARD_STOP_MS;
    this.fastPollMs = config.fastPollMs ?? FAST_POLL_MS;
    this.fastWindowMs = config.fastWindowMs ?? FAST_WINDOW_MS;
    this.slowPollMs = config.slowPollMs ?? SLOW_POLL_MS;
    this.sleep = config.sleep ?? defaultSleep;
    this.now = config.now ?? (() => Date.now());
    this.desktopLaunch =
      config.desktopLaunch ??
      ((launchId, capsuleRef) => requestDesktopLaunch(launchId, capsuleRef));
  }

  /** One request path: cookie auth, JSON, single error map. */
  private async request<T>(
    path: string,
    method: "GET" | "POST",
    body?: unknown,
    analyticsIdentity = false,
  ): Promise<T> {
    const headers: Record<string, string> =
      // Identity headers are attached ONLY to the request that actually
      // triggers a server-recorded event (POST /v1/launches), never to the
      // status poll. Polling is the hot path (300ms for the first 10s), and
      // a custom header would make every cross-origin poll pay a CORS
      // preflight round-trip.
      analyticsIdentity ? analyticsIdentityHeaders() : {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}${path}`, withConnectionAuth({
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        // The account session cookie authorizes every launch call.
      }));
    } catch (err) {
      throw new OpenCapsuleError(
        "network",
        new LaunchConnectionError(err).message,
      );
    }
    let json: unknown = null;
    try {
      json = await res.json();
    } catch {
      json = null;
    }
    if (!res.ok) {
      const api: LaunchApiError = launchApiErrorFromBody(res.status, json);
      const blocked = blockedDetailFromBody(json);
      throw new OpenCapsuleError(kindForStatus(res.status), api.message, {
        status: api.status,
        code: api.code,
        blockers: blocked.blockers,
        launchId: blocked.launchId,
        missingCapabilities: api.missingCapabilities,
        offer: api.offer,
      });
    }
    return json as T;
  }

  /**
   * Open (or resume/attach) a capsule and wait for the outcome.
   *
   * POST /v1/launches → if inside Ato Desktop with a launch-capable bridge,
   * hand the launch_id off (short ack timeout); an ACCEPTED ack ends here —
   * the desktop owns visibility and the PWA never polls. Otherwise (browser,
   * old desktop, rejected/timed-out ack) the single poll loop runs to
   * ready/blocked/terminal or the hard stop. A blocked POST result
   * short-circuits (nothing is starting; re-POST resumes the same launch id).
   */
  async openCapsule(
    capsuleRef: string,
    opts: OpenCapsuleOptions = {},
  ): Promise<OpenCapsuleResult> {
    const startedAt = this.now();
    // Every submission carries an idempotency key (caller's, else a fresh
    // one per call): the server's (owner, idempotency_key) unique index makes
    // any transport/event-duplicated POST of THIS submission converge on one
    // launch (the staging E2E saw one Run click yield two POSTs → a ghost
    // blocked launch stole the card UI). Distinct clicks get distinct keys —
    // re-opening after a stop must mint a new launch, so the key is per-call,
    // never a stable per-capsule constant.
    const idempotencyKey = opts.idempotencyKey ?? newIdempotencyKey();
    let launch: LaunchStatus;
    let postJson: unknown;
    try {
      markSnapshotTiming("launch_post_sent_at");
      postJson = await this.request<unknown>(ROUTE, "POST", {
        capsule_ref: capsuleRef,
        // Legacy compat fields kept until the pre-v3 servers are gone.
        intent: "open",
        preferred_surface: "auto",
        accepted_session_surfaces: PWA_ACCEPTED_SESSION_SURFACES,
        ...(opts.targetLabel ? { target_label: opts.targetLabel } : {}),
        ...(opts.profile ? { profile: opts.profile } : {}),
        ...(opts.execution ? { execution: opts.execution } : {}),
        ...(opts.placement ? { placement: opts.placement } : {}),
        ...(opts.origin ? { origin: opts.origin } : {}),
        ...(opts.aiByok ? { ai_byok: true } : {}),
        idempotency_key: idempotencyKey,
      }, true);
      markSnapshotTiming("launch_post_response_at");
      // A Static Web delivery short-circuits the ENTIRE launch flow: the
      // server returned a CDN URL instead of a workload, so there is no launch
      // to observe, hand off, or poll. Detect it before the LaunchStatus parse.
      const staticWeb = normalizeLaunchStaticDelivery(postJson);
      if (staticWeb) return { kind: "static_web", ...staticWeb };
      launch = postJson as LaunchStatus;
      this.observe(launch);
    } catch (err) {
      // THE single 402 stash call site: remember what the user was opening so
      // the billing return screen can resume it after checkout.
      if (err instanceof OpenCapsuleError && err.kind === "billing_required") {
        savePendingCloudLaunch({
          capsuleRef,
          source: opts.origin === "run" ? "run" : "open",
          intent: "open",
        });
      }
      throw err;
    }
    opts.onUpdate?.(launch);

    // Blocked short-circuits BEFORE the desktop gate — nothing is starting,
    // so there is nothing to hand off or poll.
    const settled = this.settle(launch, capsuleRef);
    if (settled && settled.kind !== "ready") return settled;

    // Desktop gate (plan §4): hand the launch_id (never an app_url/token) to
    // the desktop right after the POST. Accepted ack → the desktop owns the
    // launch; not accepted / timeout / unavailable → keep polling here so the
    // launch is never orphaned.
    const ack = await this.desktopLaunch(launch.launch_id, capsuleRef);
    if (ack.accepted) {
      return {
        kind: "handed_off",
        launch_id: launch.launch_id,
        app: this.toOpenApp(launch, capsuleRef, "desktop_native"),
      };
    }

    if (settled) return settled; // already ready on POST (attach/resume)

    // ── the single poll loop ──
    while (true) {
      if (opts.shouldAbort?.()) {
        throw new OpenCapsuleError("aborted", "The launch watch was aborted.");
      }
      const elapsed = this.now() - startedAt;
      if (elapsed >= this.hardStopMs) {
        throw new OpenCapsuleError(
          "timeout",
          "Timed out waiting for the app to be ready.",
          { launchId: launch.launch_id },
        );
      }
      await this.sleep(
        elapsed < this.fastWindowMs ? this.fastPollMs : this.slowPollMs,
      );
      if (opts.shouldAbort?.()) {
        throw new OpenCapsuleError("aborted", "The launch watch was aborted.");
      }
      launch = await this.status(launch.launch_id);
      opts.onUpdate?.(launch);
      const outcome = this.settle(launch, capsuleRef);
      if (outcome) return outcome;
    }
  }

  /** GET /v1/launches/:id — one status read (the poll's building block). */
  async status(launchId: string): Promise<LaunchStatus> {
    const launch = await this.request<LaunchStatus>(
      `${ROUTE}/${encodeURIComponent(launchId)}`,
      "GET",
    );
    this.observe(launch);
    return launch;
  }

  /** Index every observed status's run_id ↔ launch_id pairing so stop paths
   *  that only know a run_id can still go through the stop facade. */
  private observe(launch: LaunchStatus): void {
    if (launch.run_id && launch.launch_id) {
      rememberLaunchForRun(launch.run_id, launch.launch_id);
    }
  }

  /** POST /v1/launches/:id/stop — stop a ready/running launch (facade;
   *  normalizes to stopping/stopped whatever the backing provider is). */
  stop(launchId: string): Promise<{ launch_id: string; state: LaunchState }> {
    markSnapshotTiming("stop_sent_at");
    return this.request(
      `${ROUTE}/${encodeURIComponent(launchId)}/stop`,
      "POST",
      {},
    );
  }

  /** POST /v1/launches/:id/cancel — abort a blocked/starting launch (also the
   *  orphan-cleanup path after a failed desktop handoff). */
  cancel(launchId: string): Promise<{ launch_id: string; cancelled: boolean }> {
    return this.request(
      `${ROUTE}/${encodeURIComponent(launchId)}/cancel`,
      "POST",
      {},
    );
  }

  /** Terminal-state classification for one observed status; null = keep
   *  waiting. Terminal-aware INCLUDING blocked (plan §2.7). */
  private settle(
    launch: LaunchStatus,
    capsuleRef: string,
  ): OpenCapsuleResult | null {
    const surface =
      launch.state === "ready" ? sessionSurfaceFromEnvelope(launch) : null;
    if (launch.state === "ready" && surface) {
      markSnapshotTiming("first_ready_seen_at");
      return {
        kind: "ready",
        launch,
        app: {
          ...this.toOpenApp(launch, capsuleRef, undefined),
          state: "ready",
          surface,
        },
      };
    }
    if (launch.state === "blocked") {
      return {
        kind: "blocked",
        launch_id: launch.launch_id,
        blockers: launch.blockers ?? [],
        launch,
      };
    }
    if (
      launch.state === "failed" ||
      launch.state === "stopping" ||
      launch.state === "stopped" ||
      launch.state === "cancelled" ||
      launch.state === "expired"
    ) {
      return { kind: "failed", state: launch.state, launch };
    }
    return null; // starting (or a legacy ready response without a surface/app_url) → keep polling
  }

  /** Server DTO → the converged OpenApp (identity = launch_id). */
  private toOpenApp(
    launch: LaunchStatus,
    capsuleRef: string,
    openMode: OpenApp["open_mode"] | undefined,
  ): OpenApp {
    const surface = sessionSurfaceFromEnvelope(launch) ?? undefined;
    const webSurface = surface?.kind === "web" ? surface : undefined;
    const embedPolicy = webSurface?.descriptor.embedPolicy ?? "sandboxed";
    const appUrl = webSurface?.access.connectUrl;
    return {
      launch_id: launch.launch_id,
      run_id: launch.run_id ?? undefined,
      capsule_ref: launch.capsule_ref ?? capsuleRef,
      title: launch.capsule_ref ?? capsuleRef,
      state: launch.state === "ready" ? "ready" : "starting",
      app_url: appUrl,
      surface,
      open_mode:
        openMode ??
        (webSurface && embedPolicy === "external_only"
          ? "external"
          : "embedded"),
      embed_policy: embedPolicy,
      stop_url: launch.stop_url,
    };
  }
}
